import { useEffect, useRef } from "react";
import { getStoredLeadId } from "@/lib/leadIdCookie";
import { trackVideoWatchedPercentage } from "@/lib/leadEvents";
import { getNextMilestonesToReport } from "@/lib/videoEngagement";
import { VIDEO_WATCH_MILESTONES } from "./heroVideo.js";

const THROTTLE_MS = 1000;

function getSessionMaxKey(leadId) {
  return `fl_video_pct_max_${leadId}`;
}

function readSessionMax(leadId) {
  const raw = sessionStorage.getItem(getSessionMaxKey(leadId));
  const parsed = Number.parseInt(raw ?? "", 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function writeSessionMax(leadId, maxSent) {
  sessionStorage.setItem(getSessionMaxKey(leadId), String(maxSent));
}

export function useVideoEngagementTracking(videoRef, { enabled = false } = {}) {
  const maxSentRef = useRef(0);
  const lastTickRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    const leadId = getStoredLeadId();
    if (!leadId) {
      return undefined;
    }

    maxSentRef.current = Math.max(maxSentRef.current, readSessionMax(leadId));

    const handleTimeUpdate = () => {
      const now = Date.now();
      if (now - lastTickRef.current < THROTTLE_MS) {
        return;
      }
      lastTickRef.current = now;

      if (!video.duration || !Number.isFinite(video.duration)) {
        return;
      }

      const watchedPercent = Math.floor((video.currentTime / video.duration) * 100);
      const milestones = getNextMilestonesToReport(
        maxSentRef.current,
        watchedPercent,
        VIDEO_WATCH_MILESTONES,
      );

      milestones.forEach((milestone) => {
        trackVideoWatchedPercentage(leadId, milestone)
          .then(() => {
            maxSentRef.current = Math.max(maxSentRef.current, milestone);
            writeSessionMax(leadId, maxSentRef.current);
          })
          .catch((err) => {
            console.warn("[VideoEngagement] video_watched_percentage failed", err);
          });
      });
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [enabled, videoRef]);
}
